import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Heart,
  Tag,
  Clock
} from 'lucide-react';
import { format, isAfter, isBefore } from 'date-fns';
import { getEventImageUrl } from '../../../services/storageService';

const EventCard = ({ event, isFavorited: initialFavorited = false }) => {
  const navigate = useNavigate();
  const [isFavorited, setIsFavorited] = useState(event?.isFavorited || initialFavorited);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const handleCardClick = () => {
    // Use query parameter navigation to match EventDetails component
    navigate(`/event-details?id=${event?.id}`);
  };

  const handleFavoriteClick = (e) => {
    e?.stopPropagation();
    setIsFavorited(!isFavorited);
    // TODO: Integrate with favorites API when available
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  // Calculate event status
  const getEventStatus = () => {
    if (!event?.date && !event?.event_date) return 'upcoming';
    
    const eventDate = new Date(event?.date || event?.event_date);
    const now = new Date();
    
    if (isBefore(eventDate, now)) return 'past';
    if (isAfter(eventDate, now)) return 'upcoming';
    return 'live';
  };

  const eventStatus = getEventStatus();
  const isUpcoming = eventStatus === 'upcoming';
  const isPast = eventStatus === 'past';

  // Get minimum price
  const getMinPrice = () => {
    if (!event?.tiers || event?.tiers?.length === 0) return 0;
    return Math.min(...event?.tiers?.map(t => t?.price || 0));
  };

  const minPrice = getMinPrice();

  // Get event image URL - handles both storage URLs and direct URLs
  const getImageSrc = () => {
    if (event?.image_url) {
      // If it's already a full URL (starts with http), use it directly
      if (event?.image_url?.startsWith('http')) {
        return event?.image_url;
      }
      // If it's a storage path, get the public URL
      return getEventImageUrl(event?.image_url);
    }
    // Fallback to event.image for backward compatibility
    return event?.image || null;
  };

  const imageSrc = getImageSrc();

  return (
    <div
      onClick={handleCardClick}
      className={`
        bg-card rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 
        cursor-pointer group border border-border hover:border-primary/50 relative
        ${isPast ? 'opacity-75' : ''}
      `}
    >
      {/* Event Image */}
      <div className="relative h-48 overflow-hidden">
        {imageSrc && !imageError ? (
          <div className="relative">
            {imageLoading && (
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                <div className="animate-pulse">
                  <Calendar className="w-8 h-8 text-primary/40" />
                </div>
              </div>
            )}
            <img
              src={imageSrc}
              alt={event?.title || 'Event image'}
              className={`
                w-full h-full object-cover group-hover:scale-105 transition-all duration-300
                ${imageLoading ? 'opacity-0' : 'opacity-100'}
              `}
              onError={handleImageError}
              onLoad={handleImageLoad}
              loading="lazy"
            />
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            <div className="text-center">
              <Calendar className="w-12 h-12 text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Event Image</p>
            </div>
          </div>
        )}
        
        {/* Favorite Button */}
        <button
          onClick={handleFavoriteClick}
          className={`
            absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-all duration-200
            ${isFavorited 
              ? 'bg-red-500/90 text-white hover:bg-red-600/90' :'bg-black/30 text-white hover:bg-black/50'
            }
          `}
        >
          <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
        </button>

        {/* Status Badge */}
        {isPast && (
          <div className="absolute top-3 left-3">
            <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
              Past Event
            </span>
          </div>
        )}

        {event?.is_featured && (
          <div className="absolute top-3 left-3">
            <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
              Featured
            </span>
          </div>
        )}

        {/* Category Badge */}
        <div className="absolute bottom-3 left-3">
          <span className="bg-black/70 text-white px-3 py-1 rounded-full text-xs capitalize">
            {event?.category}
          </span>
        </div>
      </div>
      
      {/* Event Content */}
      <div className="p-4">
        {/* Title */}
        <h3 className="text-lg font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {event?.title}
        </h3>

        {/* Event Details */}
        <div className="space-y-2 mb-4">
          {/* Date */}
          <div className="flex items-center text-sm text-muted-foreground">
            <Calendar className="w-4 h-4 mr-2 text-primary flex-shrink-0" />
            <span className="line-clamp-1">
              {(event?.date || event?.event_date)
                ? format(new Date(event?.date || event?.event_date), 'MMM dd, yyyy')
                : 'Date TBD'
              }
            </span>
          </div>

          {/* Time */}
          {(event?.date || event?.event_date) && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Clock className="w-4 h-4 mr-2 text-primary flex-shrink-0" />
              <span>
                {format(new Date(event?.date || event?.event_date), 'h:mm a')}
              </span>
            </div>
          )}

          {/* Location */}
          <div className="flex items-center text-sm text-muted-foreground">
            <MapPin className="w-4 h-4 mr-2 text-primary flex-shrink-0" />
            <span className="line-clamp-1">
              {event?.location || event?.venue_name || 'Location TBD'}
            </span>
          </div>

          {/* Attendees */}
          <div className="flex items-center text-sm text-muted-foreground">
            <Users className="w-4 h-4 mr-2 text-primary flex-shrink-0" />
            <span>
              {event?.attendeeCount || event?.attendees || 0} 
              {(event?.maxCapacity || event?.max_capacity) ? ` / ${event?.maxCapacity || event?.max_capacity}` : ''} attendees
            </span>
          </div>
        </div>

        {/* Organizer */}
        <div className="mb-4">
          <p className="text-xs text-muted-foreground">
            By <span className="text-foreground font-medium">
              {event?.organizer || event?.host?.full_name || 'Event Organizer'}
            </span>
          </p>
        </div>

        {/* Price and Action */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-primary" />
            {minPrice > 0 ? (
              <div className="text-sm">
                <span className="text-muted-foreground">From </span>
                <span className="text-lg font-bold text-primary">
                  ${minPrice}
                </span>
              </div>
            ) : (
              <div className="text-sm">
                <span className="text-lg font-bold text-green-600">Free</span>
              </div>
            )}
          </div>

          <button
            onClick={(e) => {
              e?.stopPropagation();
              handleCardClick();
            }}
            disabled={isPast}
            className={`
              px-4 py-2 rounded-md text-sm font-semibold transition-colors
              ${isPast 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' :'bg-primary text-primary-foreground hover:bg-primary/90'
              }
            `}
          >
            {isPast ? 'View Details' : 'Learn More'}
          </button>
        </div>

        {/* Capacity Warning */}
        {isUpcoming && (event?.maxCapacity || event?.max_capacity) && (event?.attendeeCount || event?.attendees) && (
          <div className="mt-3">
            {((event?.attendeeCount || event?.attendees) / (event?.maxCapacity || event?.max_capacity)) >= 0.8 && (
              <div className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-md">
                Almost full! Only {(event?.maxCapacity || event?.max_capacity) - (event?.attendeeCount || event?.attendees)} spots left
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;